import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';

// Proposal for Aditya Joshi (design studio). Full-screen deck, one idea per
// screen. Built from his 5-question doc (filled 2026-09-17) plus the call on
// 2026-09-15. No pricing on the page by design, the number is named on the call.

const SLIDES = ['Your answers', 'The position', 'The check', 'The format', 'The blocker', 'The work', 'The promise'];

const Eyebrow = ({ children }) => (
    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-6">{children}</div>
);

const Slide = ({ id, children, className = '' }) => (
    <section
        id={id}
        className={`snap-start min-h-[100svh] w-full flex items-center px-6 sm:px-10 py-24 ${className}`}
    >
        <div className="w-full max-w-3xl mx-auto">{children}</div>
    </section>
);

const Row = ({ k, v, tone = 'white', href }) => {
    const toneClass = tone === 'rose' ? 'text-rose-400' : tone === 'emerald' ? 'text-emerald-400' : 'text-indigo-400';
    return (
        <div className="flex items-baseline gap-4 sm:gap-6 border-t border-white/10 py-4">
            <div className={`shrink-0 w-24 sm:w-32 text-xs sm:text-sm font-bold tracking-wide ${toneClass}`}>
                {href ? (
                    <a
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 hover:text-white transition-colors underline decoration-dotted underline-offset-4 break-all"
                    >
                        {k}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                ) : (
                    k
                )}
            </div>
            <div className="text-slate-300 text-sm sm:text-base leading-relaxed">{v}</div>
        </div>
    );
};

export default function AdityaStrategy() {
    const scroller = useRef(null);
    const [active, setActive] = useState(0);

    useEffect(() => {
        document.title = 'SocialSEO | Aditya Joshi';
    }, []);

    useEffect(() => {
        const el = scroller.current;
        if (!el) return undefined;
        const onScroll = () => {
            const i = Math.round(el.scrollTop / el.clientHeight);
            setActive(Math.max(0, Math.min(SLIDES.length - 1, i)));
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    const go = (i) => {
        const el = scroller.current;
        if (el) el.scrollTo({ top: i * el.clientHeight, behavior: 'smooth' });
    };

    return (
        <div className="font-sans text-white h-[100svh] overflow-hidden relative selection:bg-indigo-500/30">
            <div className="fixed inset-0 bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-25%] left-[-15%] w-[65%] h-[65%] bg-indigo-600/20 blur-[170px] rounded-full mix-blend-screen" />
                <div className="absolute bottom-[-25%] right-[-15%] w-[65%] h-[65%] bg-emerald-600/15 blur-[170px] rounded-full mix-blend-screen" />
            </div>

            <header className="fixed top-0 left-0 right-0 z-50 px-6 sm:px-10 py-5 bg-[#07090E]/70 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-2 max-w-3xl mx-auto">
                    <span className="font-bold tracking-tight text-lg">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-1">/</span>
                    <span className="text-white/70 font-medium text-sm truncate">Aditya Joshi</span>
                </div>
            </header>

            {/* Progress dots */}
            <div className="hidden md:flex fixed right-7 top-1/2 -translate-y-1/2 z-50 flex-col gap-3">
                {SLIDES.map((label, i) => (
                    <button
                        key={label}
                        onClick={() => go(i)}
                        aria-label={label}
                        className={`w-2 h-2 rounded-full transition-all ${i === active ? 'bg-indigo-400 scale-150' : 'bg-white/25 hover:bg-white/50'}`}
                    />
                ))}
            </div>

            <div ref={scroller} className="h-[100svh] overflow-y-scroll snap-y snap-mandatory scroll-smooth">

                {/* 1. Hero */}
                <Slide id="answers">
                    <Eyebrow>After your five answers</Eyebrow>
                    <h1 className="text-[2.6rem] sm:text-6xl font-black tracking-tight leading-[1.02] mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 pb-1">
                        You said you don&rsquo;t<br />have a niche.
                    </h1>
                    <p className="text-white/80 text-lg sm:text-2xl font-medium leading-snug mb-8">
                        You wrote it down three times without noticing.
                    </p>
                    <div>
                        <Row k="Q1" v="Food, one of the five things you are into." />
                        <Row k="Q2" v="D2C brands willing to experiment." />
                        <Row k="Q4" v="F&B is the work you can put on camera." />
                    </div>
                </Slide>

                {/* 2. The position */}
                <Slide id="position">
                    <Eyebrow>The position</Eyebrow>
                    <h2 className="text-[2.2rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8 text-emerald-300">
                        The design studio for<br />food and drink brands.
                    </h2>
                    <p className="text-white/70 text-base sm:text-xl leading-relaxed mb-8">
                        Not a general studio that also does food. The one food founders think of first.
                    </p>
                    <div>
                        <Row tone="emerald" k="You like it" v="It was your own answer, so it is not a costume." />
                        <Row tone="emerald" k="You can show it" v="The niche and your NDA line agree for once." />
                        <Row tone="emerald" k="It buys design" v="Packaging, menus, launches. Constantly, and on retainer." />
                    </div>
                </Slide>

                {/* 3. The research */}
                <Slide id="check">
                    <Eyebrow>We checked the seven accounts you sent</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Designers do not hire<br />design studios.
                    </h2>
                    <div className="mb-7">
                        <Row tone="rose" k="rishabhjdesigns" href="https://www.instagram.com/rishabhjdesigns/" v="Bio says freelance coach. Runs webinars for design students." />
                        <Row tone="rose" k="hustlerani" href="https://www.instagram.com/hustlerani/" v="Workshops for creators. Sells to people who want her job." />
                        <Row k="bennettlooper" href="https://www.instagram.com/bennettlooper/" v="Closest to useful. His content is the work, not lessons about it." />
                    </div>
                    <p className="text-white/70 text-base sm:text-lg leading-relaxed">
                        Two of the three earn from people like them. That is a craft list, not a business model.
                        Copy it and you collect designers, which is the opposite of the quality inquiries you asked for.
                    </p>
                </Slide>

                {/* 4. The format */}
                <Slide id="format">
                    <Eyebrow>How you show up</Eyebrow>
                    <h2 className="text-[2.2rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        On camera when it counts.<br />Never on a schedule.
                    </h2>
                    <p className="text-white/70 text-base sm:text-xl leading-relaxed mb-8">
                        Four formats in the mix, so the page never waits on your calendar.
                    </p>
                    <div>
                        <Row k="01 Your team films" v="Shoots, packaging tests, a menu on its sixth version. The camera is already out." />
                        <Row k="02 Interactive" v="You and your team on camera together. Nobody has to carry a video alone." />
                        <Row k="03 The founders" v="Your own answer. A case study and a referral engine at once." />
                        <Row tone="emerald" k="04 Your clone" v="For the weeks you have no time. The talking head still runs, without you in the room." />
                    </div>
                </Slide>

                {/* 5. The blocker */}
                <Slide id="blocker">
                    <Eyebrow>Before we turn the tap on</Eyebrow>
                    <h2 className="text-[2.1rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8 text-amber-300">
                        You want 20 inquiries.<br />You have nothing to sell them.
                    </h2>
                    <p className="text-white/80 text-base sm:text-xl leading-relaxed mb-5">
                        You told us there is no specific offer yet, that it is scoped per client. That works at six
                        clients who found you by referral. It breaks at twenty strangers a month.
                    </p>
                    <p className="text-white/60 text-base sm:text-lg leading-relaxed">
                        Your hands were already full. That is why you never marketed. So week one builds one named
                        offer with a fixed shape, before anything publishes.
                    </p>
                </Slide>

                {/* 6. The work */}
                <Slide id="work">
                    <Eyebrow>Three months</Eyebrow>
                    <h2 className="text-[2.2rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        Narrow on purpose.
                    </h2>
                    <div className="mb-7">
                        <Row tone="emerald" k="Week one" v="Positioning locked and one offer named. Applies to the six clients you already have." />
                        <Row k="Your clone" v="One session. Your time stops being the bottleneck." />
                        <Row k="30 a month" v="Researched, scripted, edited as one visual system." />
                        <Row k="The funnel" v="Comment to DM through our Meta verified app, plus the resource behind it." />
                        <Row tone="emerald" k="The room" v="A space for the founders who reply, set up and seeded so it is not just an inbox." />
                        <Row k="Monthly" v="What pulled, what did not, where it leaked." />
                    </div>
                    <p className="text-white/50 text-sm sm:text-base leading-relaxed">
                        Your founder page, not the studio page. The food lane, not everything you do.
                    </p>
                </Slide>

                {/* 7. The promise */}
                <Slide id="promise">
                    <Eyebrow>What you are actually buying</Eyebrow>
                    <h2 className="text-[2rem] sm:text-5xl font-black tracking-tight leading-[1.05] mb-8">
                        A system that runs.<br />
                        <span className="text-emerald-300">Then a room that is yours.</span>
                    </h2>
                    <div className="mb-10">
                        <Row k="The system" v="Research, scripts, filming days, edits, posting, reporting. It keeps running on the weeks you are heads down on client work." />
                        <Row tone="emerald" k="The positioning" v="You stop being a studio that does everything and become the one food founders name first." />
                        <Row tone="emerald" k="The community" v="Not followers. A small core of food founders who start talking to each other, not only to you. That is the asset that outlives the content." />
                    </div>
                    <p className="text-white/50 text-sm sm:text-base leading-relaxed mb-10">
                        We own the audience and the conversations, and we report them weekly. You own the close.
                    </p>
                    <p className="text-indigo-300 text-base sm:text-xl font-bold leading-snug">
                        One question left, and it is yours to answer.<br />
                        <span className="text-white">Which food brand in your portfolio can we lead with?</span>
                    </p>
                </Slide>
            </div>

            {/* Scroll hint */}
            {active === 0 && (
                <div className="fixed bottom-7 left-1/2 -translate-x-1/2 z-40 text-white/40 animate-bounce pointer-events-none">
                    <ChevronDown className="w-6 h-6" />
                </div>
            )}
        </div>
    );
}
